import { FileText } from 'lucide-react';
import { useLanguage } from '@/i18n/index';

const PRIORITY_LABELS = {
  INFO: { label: 'Info', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  WATCH: { label: 'À surveiller', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  ALERT: { label: 'Alerte', cls: 'bg-orange-50 text-orange-800 border-orange-200' },
  CRITICAL: { label: 'Critique', cls: 'bg-red-50 text-red-800 border-red-200' },
};

const ACTION_LABELS = {
  NONE: 'Aucune action',
  CALL_PATIENT: 'Rappeler patient',
  DOCTOR_REVIEW: 'Avis médecin',
  APPOINTMENT: 'RDV à prévoir',
  SECRETARY_TASK: 'Tâche secrétaire',
  URGENT_ESCALATION: 'Escalade urgente',
  ADHERENCE_REVIEW: 'Revoir observance',
  PROTOCOL_REVIEW: 'Revoir protocole',
};

const TAG_LABELS = {
  fatigue: 'Fatigue', pain: 'Douleur', abdominal_pain: 'Douleur abdominale', headache: 'Céphalée', nausea: 'Nausée',
  vomiting: 'Vomissement', diarrhea: 'Diarrhée', constipation: 'Constipation', fever: 'Fièvre', chills: 'Frissons',
  dyspnea: 'Dyspnée', cough: 'Toux', mouth_sores: 'Mucite', dry_mouth: 'Bouche sèche', taste_change: 'Goût',
  appetite_loss: 'Appétit', insomnia: 'Sommeil', anxiety: 'Anxiété', sadness: 'Tristesse', neuropathy: 'Neuropathie',
  dizziness: 'Vertiges', rash: 'Rash', dry_skin: 'Peau sèche', hand_foot: 'Main-pied', hair_loss: 'Cheveux',
  bruising: 'Ecchymoses', bleeding: 'Saignement', epistaxis: 'Épistaxis', edema: 'Œdème', concentration: 'Concentration',
  memory: 'Mémoire', sexual_health: 'Sexualité', oral_adherence: 'Observance', missed_dose: 'Dose oubliée',
  treatment_tolerance: 'Tolérance', hospitalization: 'Hospitalisation', er_visit: 'Urgences', appointment_need: 'RDV',
  document_need: 'Document', social_need: 'Social', callback_need: 'Rappel',
};

/**
 * Affichage des dernières transmissions du patient
 * Utilisé par NursePatientDetail et DoctorPatientDetail
 */
export default function TransmissionsCard({ transmissions, maxVisible = 5 }) {
  const { t } = useLanguage();

  const CATEGORY_STYLES = {
    NOTE: { bg: "border-slate-400", label: "Note" },
    OBSERVATION: { bg: "border-blue-500", label: "Observation" },
    ALERTE: { bg: "border-orange-500", label: "Alerte" },
    CONSIGNE: { bg: "border-purple-500", label: "Consigne" },
  };

  return (
    <div className="dash-card md:col-span-2" data-testid="transmissions-card">
      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2" data-testid="transmissions-card-title">
        <FileText className="w-5 h-5 text-purple-600" />
        Dernières transmissions
      </h3>
      {transmissions.length === 0 ? (
        <p className="text-slate-400 text-center py-4" data-testid="transmissions-empty-state">Aucune transmission</p>
      ) : (
        <div className="space-y-3 max-h-64 overflow-y-auto" data-testid="transmissions-list">
          {transmissions.slice(0, maxVisible).map((tr, i) => {
            const cat = CATEGORY_STYLES[tr.category] || CATEGORY_STYLES.NOTE;
            const priority = PRIORITY_LABELS[tr.priority || 'INFO'] || PRIORITY_LABELS.INFO;
            const manualTags = tr.clinicalTags || [];
            const suggestedTags = tr.suggestedClinicalTags || [];
            const allTags = [...manualTags.map((tag) => [tag, false]), ...suggestedTags.map((tag) => [tag, true])].slice(0, 8);
            return (
              <div key={tr.id || i} className={`p-3 bg-slate-50 rounded-lg border-l-4 ${tr.statusChange ? "border-emerald-500" : cat.bg}`} data-testid={`transmission-item-${tr.id || i}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-600" data-testid={`transmission-author-${tr.id || i}`}>{tr.authorName || "Soignant"}</span>
                    {tr.category && !tr.statusChange && (
                      <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded" data-testid={`transmission-category-${tr.id || i}`}>{cat.label}</span>
                    )}
                    {!tr.statusChange && (
                      <span className={`text-[10px] font-bold border px-1.5 py-0.5 rounded ${priority.cls}`} data-testid={`transmission-priority-${tr.id || i}`}>{priority.label}</span>
                    )}
                  </div>
                  <span className="text-xs text-slate-400" data-testid={`transmission-date-${tr.id || i}`}>
                    {new Date(tr.createdAt).toLocaleDateString("fr-FR", {
                      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"
                    })}
                  </span>
                </div>
                {tr.statusChange && (
                  <p className="text-xs text-emerald-600 mb-1" data-testid={`transmission-status-change-${tr.id || i}`}>
                    Statut: {t(`status.${tr.oldStatus}`)} → {t(`status.${tr.newStatus}`)}
                  </p>
                )}
                <p className="text-sm text-slate-700" data-testid={`transmission-content-${tr.id || i}`}>{tr.content}</p>
                {!tr.statusChange && (allTags.length > 0 || tr.actionRequired !== 'NONE' || tr.treatmentRelated || tr.oralAdherenceRelated) && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`transmission-data-tags-${tr.id || i}`}>
                    {allTags.map(([tag, suggested]) => (
                      <span key={`${tag}-${suggested}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${suggested ? 'bg-white text-slate-500 border border-dashed border-slate-300' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`} data-testid={`transmission-tag-${tag}-${tr.id || i}`}>
                        {TAG_LABELS[tag] || tag}{suggested ? ' · détecté' : ''}
                      </span>
                    ))}
                    {tr.actionRequired && tr.actionRequired !== 'NONE' && (
                      <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700" data-testid={`transmission-action-${tr.id || i}`}>{ACTION_LABELS[tr.actionRequired] || tr.actionRequired}</span>
                    )}
                    {tr.treatmentRelated && <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-bold text-violet-700" data-testid={`transmission-treatment-related-${tr.id || i}`}>Traitement</span>}
                    {tr.oralAdherenceRelated && <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800" data-testid={`transmission-oral-related-${tr.id || i}`}>Observance</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
